import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, MessageCircle, CreditCard, Shield } from 'lucide-react';
import Layout from '../components/Layout';
import { CONTACT_LINKS } from '../config/siteContact';

const Channel = ({ icon: Icon, title, desc, href, label, accent = 'cyan' }) => (
  <a
    href={href}
    target={href.startsWith('http') ? '_blank' : undefined}
    rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
    className="neon-card rounded-lg p-5 bracket-corners block hover:no-underline group"
  >
    <div
      className={`w-10 h-10 rounded grid place-items-center mb-3 ${
        accent === 'orange'
          ? 'bg-orange-500/10 border border-orange-500/40'
          : 'bg-cyan-400/10 border border-cyan-400/40'
      }`}
    >
      <Icon className={`w-5 h-5 ${accent === 'orange' ? 'text-orange-300' : 'text-cyan-300'}`} />
    </div>
    <h3 className="font-mono-display text-lg text-white mb-1 group-hover:text-cyan-200 transition-colors">{title}</h3>
    <p className="text-sm text-zinc-400 leading-relaxed mb-3">{desc}</p>
    <span className={`text-sm font-semibold ${accent === 'orange' ? 'text-orange-300' : 'text-cyan-300'}`}>{label}</span>
  </a>
);

export default function Contact() {
  return (
    <Layout>
      <section className="max-w-3xl mx-auto px-6 py-10 sm:py-12">
        <Link to="/" className="btn-ghost inline-flex mb-6">
          <ArrowLeft className="w-3.5 h-3.5" />
          Back to home
        </Link>

        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cyan-400/40 bg-cyan-400/5 text-cyan-200 text-xs uppercase tracking-widest mb-4">
          <Mail className="w-3.5 h-3.5" />
          Support // contact
        </div>
        <h1 className="font-mono-display text-3xl sm:text-4xl text-white mb-3">
          Get in <span className="neon-orange">touch</span>
        </h1>
        <p className="text-zinc-400 text-sm sm:text-base max-w-xl leading-relaxed mb-10">
          Questions about your vault, billing, or account linking? Reach us through the channels below. We typically reply within 24–48 hours.
        </p>

        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          <Channel
            icon={Mail}
            title="Email"
            desc="General support, privacy requests, and account help."
            href={`mailto:${CONTACT_LINKS.email}`}
            label={CONTACT_LINKS.email}
          />
          <Channel
            icon={MessageCircle}
            title="Discord"
            desc="Community help, payment issues, and bug reports."
            href={CONTACT_LINKS.discordUrl}
            label="Join the server"
            accent="orange"
          />
        </div>

        <div className="neon-card rounded-lg p-6 space-y-4">
          <div className="flex items-start gap-3">
            <CreditCard className="w-5 h-5 text-orange-300 shrink-0 mt-0.5" />
            <div>
              <div className="font-mono-display text-white text-sm mb-1">Billing & plans</div>
              <p className="text-sm text-zinc-400 leading-relaxed">
                Manual PKR payments are verified by our team. Include your username and transaction reference when you submit proof on the{' '}
                <Link to="/pricing" className="text-cyan-300 hover:text-cyan-200">billing page</Link>.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3 border-t border-zinc-800 pt-4">
            <Shield className="w-5 h-5 text-cyan-300 shrink-0 mt-0.5" />
            <div>
              <div className="font-mono-display text-white text-sm mb-1">Legal</div>
              <p className="text-sm text-zinc-400 leading-relaxed">
                <Link to="/terms-of-service" className="text-cyan-300 hover:text-cyan-200">Terms of Service</Link>
                {' · '}
                <Link to="/privacy-policy" className="text-cyan-300 hover:text-cyan-200">Privacy Policy</Link>
              </p>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
